/* eslint-disable lingui/no-unlocalized-strings, lingui/text-restrictions -- Approved hub marketing copy. */

/* Copy for the theresume.io hub: the parent page that sends job seekers to First Round and hiring
   teams to Apex 25. Only tools the brand sites already market are listed here. The scenes are
   illustrative: fictional people, no measured results. */

export type SceneId = "resume" | "spotlight" | "practice" | "ranking" | "invites" | "evidence";

export type Side = "candidate" | "recruiter";

export type Tool = {
  id: string;
  scene: SceneId;
  name: string;
  benefit: string;
  /** Where the tool lives. "app" tools open inside theresume.io; "brand" tools leave for the brand site. */
  lives: "app" | "brand";
};

export const hero = {
  titleTop: "Two sides of",
  titleBottom: "the same interview.",
  lead: "Job seekers prepare for the conversation. Hiring teams run the first round with evidence. Pick your seat.",
};

export const candidate = {
  side: "candidate" as Side,
  label: "I'm preparing for a job",
  promise: "Walk in ready.",
  brand: "First Round",
  cta: "Practise at First Round",
  more: "See all job seeker tools",
  tools: [
    {
      id: "resume",
      scene: "resume",
      name: "Resume builder",
      benefit: "Tailored to each role you apply for.",
      lives: "app",
    },
    {
      id: "spotlight",
      scene: "spotlight",
      name: "Spotlight Intro",
      benefit: "A short video introduction beside your resume.",
      lives: "app",
    },
    {
      id: "practice",
      scene: "practice",
      name: "Practice Interview",
      benefit: "Speak your answers, get feedback on each one.",
      lives: "brand",
    },
  ] satisfies Tool[],
};

export const recruiter = {
  side: "recruiter" as Side,
  label: "I'm hiring",
  promise: "Screen fairly. Decide yourselves.",
  brand: "Apex 25",
  cta: "See Apex 25",
  tools: [
    {
      id: "ranking",
      scene: "ranking",
      name: "CV ranking",
      benefit: "Rank applicants against your job criteria.",
      lives: "brand",
    },
    {
      id: "invites",
      scene: "invites",
      name: "AI first-round interviews",
      benefit: "You choose who to invite.",
      lives: "brand",
    },
    {
      id: "evidence",
      scene: "evidence",
      name: "Evidence review",
      benefit: "Video, transcript and assessment for every interview.",
      lives: "brand",
    },
  ] satisfies Tool[],
};

/** One label per scene: what the AI is doing in it. Shown in the scene's header. */
export const sceneLabels: Record<SceneId, string> = {
  resume: "AI tailoring a resume to a role",
  spotlight: "AI guiding and captioning a video",
  practice: "AI interviewer listening and giving feedback",
  ranking: "AI ranking CVs against criteria",
  invites: "You choose, AI runs the interview",
  evidence: "AI answering from the transcript",
};

export const thread = {
  eyebrow: "One interview, two chairs",
  title: "Prepared on one side. Informed on the other.",
  lead: "Both journeys run through the same kind of AI interview, which is why we build both.",
  candidateChair: "The job seeker's chair",
  recruiterChair: "The hiring team's chair",
  stages: [
    {
      id: "before",
      label: "Before",
      ai: "AI reads the role",
      candidate: {
        title: "Practise out loud",
        body: "Rehearse the questions for your role with an AI interviewer that listens.",
      },
      recruiter: {
        title: "Turn a job description into criteria",
        body: "Apex 25 ranks CVs against what the role needs.",
      },
    },
    {
      id: "during",
      label: "During",
      ai: "AI asks, people choose",
      candidate: {
        title: "Answer at your own pace",
        body: "A realistic, structured conversation with no scheduling.",
      },
      recruiter: {
        title: "Invite only who you choose",
        body: "Every invited candidate gets the same structured first-round interview.",
      },
    },
    {
      id: "after",
      label: "After",
      ai: "AI cites what was said",
      candidate: {
        title: "Hear what to improve",
        body: "Feedback on relevance, pacing and filler words, answer by answer.",
      },
      recruiter: {
        title: "Review the evidence",
        body: "Video, transcript and an assessment, each point tied to what the candidate said.",
      },
    },
    {
      id: "decision",
      label: "Decision",
      ai: "People decide",
      candidate: {
        title: "Walk in ready",
        body: "Take what you practised into the conversation that counts.",
      },
      recruiter: {
        title: "Your team decides",
        body: "AI supports the screen. People choose who moves forward.",
      },
    },
  ],
};

export const finale = {
  title: "Ready when you are.",
  lead: "AI supports. People decide.",
};

export const footer = {
  tagline: "AI-powered tools for both sides of the hiring conversation.",
  candidateHeading: "For job seekers",
  recruiterHeading: "For hiring teams",
  companyHeading: "Company",
  candidateLinks: [
    { label: "Resume builder", to: "/dashboard/resumes" },
    { label: "Spotlight Intro", to: "/dashboard/spotlight-intro" },
    { label: "All job seeker tools", to: "/job-seekers" },
  ],
  companyLinks: [
    { label: "Blog", to: "/blog" },
    { label: "Careers", to: "/careers" },
    { label: "Contact", to: "/contact" },
    { label: "Privacy", to: "/privacy" },
    { label: "Terms", to: "/terms" },
  ],
};

/** Keep in sync with THERESUME_SITE.pages["/"] in apps/server/src/brand-sites/brand-sites.ts. */
export const HUB_META = {
  title: "theResume: AI tools for job seekers and hiring teams",
  description:
    "Job seekers practise interviews, tailor resumes and record a Spotlight Intro. Hiring teams rank CVs and run structured AI first-round interviews. Choose your side.",
};

/** The first fold: one question, two doors. */
export const entry = {
  title: "What brings you here?",
  or: "or",
  cue: "See what's inside",
  candidate: {
    label: "I'm preparing for a job",
    name: "First Round",
    line: "Practise interviews with an AI that gives feedback.",
  },
  recruiter: {
    label: "I'm hiring",
    name: "Apex 25",
    line: "Rank CVs and run structured AI first-round interviews.",
  },
};
